import { StudentSkillStates } from '../../models/StudentSkillState';
import { /*Link,*/ useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { mapSkillStatus } from '../../businessLogicService/mapSkillStatus';
import { ArrowRight } from 'lucide-react';
import { useSkillIcon } from '../../custom-hooks/useSkillIcon';
import { TargetSkillTypeLabel } from '../../models/TargetSkillType';
import { SkillStatesLabel } from '../../models/SkillState';
import { DashboardRatingDisplay, SkillStatusBadge } from '../common';
import { getStarMessageWithCount } from '../../utils/skillHistoryUtils';
import { useBrainBreakNavigation } from '../../custom-hooks/useBrainBreakNavigation';
import { FC, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useInView } from 'motion/react';
import Lock from '../../assets/Lock.svg?react';
import Check from '../../assets/Check.svg?react';
import { useAppSelector } from '../../redux/hooks';
import { selectedUnit } from '../../redux/reducers/unitSlice';
import {
  blockedCardBorderColor,
  resolveUnitBaseColor,
  targetCardHighlightColors,
  unitCardHighlightColors,
  unitDarkestTextColor,
} from '../../businessLogicService/StudentDashboardAux/getColorByUnit';
import { DashboardSkillCardModal } from './DashboardSkillCardModal';
import { isCompletedStatus } from '../../businessLogicService/StudentDashboardAux/pathToSkills';
import { selectExitSlipSkillProgressFor } from '../../redux/reducers/exitSlipSkillProgressSlice';
import { DashboardCardExitSlipBadge } from './DashboardCardExitSlipBadge';
import { isSkillCompleted } from '../../businessLogicService/skillCompletionUtils';
import { selectSkillEarnedViaExitSlip } from '../../redux/reducers/exitSlipEarnedSkillsSlice';
import { SkillSubStates } from '../../models/SkillState';

interface Props {
  skillsConsolidated: StudentSkillStates;
  isBlocked?: boolean;
  id?: string;
  // Whether at least one connector on the unit path has already been passed. Forwarded to the
  // locked-skill modal so its "Go to recommended" action can reuse BackToPositionButton's scroll
  // logic, which falls back to the page top instead of a connector when nothing has been passed yet.
  hasPassedConnector?: boolean;
  // True when the student is currently practising this skill (it appears in the practice-problems
  // sessions). Surfaces a "Practicing" badge, but only on cards with no other state to show.
  isPracticing?: boolean;
}

export interface SkillModalState {
  type: 'revisit' | 'locked';
  onAccept: () => void;
}

// What a click on the card does: open one of the modal variants, or navigate straight to the skill.
// Derived from SkillModalState['type'] so adding a modal variant forces getSkillOption to place it.
type SkillCardOption = SkillModalState['type'] | 'normal';

const DashboardCard: FC<Props> = ({ skillsConsolidated, isBlocked, id, hasPassedConnector, isPracticing }) => {
  const [skillModalOption, setSkillModalOption] = useState<SkillModalState | null>(null);
  const navigate = useNavigate();
  // Drives the scroll-triggered shine: fires whenever the card (re)enters the viewport (once: false),
  // once ~30% of it is visible. The ref only attaches when the shine wrapper renders (isHighlighted),
  // but the hook must be called unconditionally to respect the rules of hooks.
  const shineRef = useRef<HTMLDivElement>(null);
  const isShineInView = useInView(shineRef, { amount: 0.3 });
  // Won by passing the last exit ticket standing in the way, and the server has not reported the new
  // skill state yet. Swapping the substate here rather than patching each place that asks about it
  // means the whole card — icon, highlight, title colour, footer — follows the one answer, and does
  // so the same way it will once the query catches up and sends this substate for real.
  const earnedViaExitSlip = useAppSelector(selectSkillEarnedViaExitSlip(skillsConsolidated?.skill?.id));
  const skillSubstateId = earnedViaExitSlip
    ? SkillSubStates.INFERRED_VIA_EXITSLIP
    : skillsConsolidated?.skillSubstate?.id;

  const { status, className, applyRatingStars } = mapSkillStatus(
    skillSubstateId,
    skillsConsolidated?.skillAssessment?.score,
  );
  const isInferred = earnedViaExitSlip || skillsConsolidated.skillSubstate.skillState?.name === 'Inferred';

  // Left undefined when the student has no Skill Challenge here, rather than defaulted to 0. The
  // footer below prefers the star message over the status whenever one exists, so defaulting would
  // hand every un-attempted skill a nought-star "Not there yet" and make the status unreachable.
  // Passed via exit slip is where that shows: it reads as "Passed" with no assessment behind it,
  // unlike Passed via assessment, which always carries a score.
  const assessmentScore = skillsConsolidated?.skillAssessment?.score;
  const starInfo = getStarMessageWithCount(assessmentScore);
  const handleBrainBreakNavigation = useBrainBreakNavigation();

  // The "Not Started" footer wrapper is hidden via `.nc_not-started { display: none }`, so a
  // practising card would hide its badge. Swap to a visible wrapper class when surfacing the
  // "Practicing" badge; every other case keeps the status-derived className.
  const footerWrapperClassName = status === 'Not Started' && isPracticing ? 'nc_practicing' : className;

  const unit = useAppSelector(selectedUnit);
  // Exit-ticket progress for this card's skill, or undefined when no exit ticket infers it — which is
  // most cards, and they get no badge. Selected per skill so replacing one skill's entry re-renders
  // that card alone rather than the whole path.
  const exitSlipProgress = useAppSelector(state =>
    selectExitSlipSkillProgressFor(state, skillsConsolidated?.skill?.id),
  );
  const isTargetCard =
    skillsConsolidated?.targetSkillType === TargetSkillTypeLabel.TARGET ||
    skillsConsolidated?.targetSkillType === TargetSkillTypeLabel.UNIT;
  const highlightColors = isTargetCard
    ? targetCardHighlightColors
    : unitCardHighlightColors[resolveUnitBaseColor(unit?.name)];
  // Highlight Inferred or Passed cards with the unit's base color, but never blocked cards.
  const isHighlighted = !isBlocked && (isInferred || status === 'Passed');
  // On highlighted (Passed/Inferred) cards the skill title takes the selected unit's darkest shade;
  // every other card keeps the default near-black.
  const titleColorClass = isHighlighted
    ? unitDarkestTextColor[resolveUnitBaseColor(unit?.name)]
    : 'tw-text-neutral-950';

  // Hover border color: blocked cards echo the gray card border; unblocked target cards use their
  // golden identity; all other cards use the current unit's base color resolved via getColorByUnit.
  const hoverBorderColor = isBlocked
    ? blockedCardBorderColor
    : isTargetCard
    ? targetCardHighlightColors.border
    : unitCardHighlightColors[resolveUnitBaseColor(unit?.name)].border;

  // Hover border widths grow past the resting border and the card grows by the same delta — with
  // matching negative margins so the footprint (and neighbours) stay put and the content never
  // contracts. Target cards rest at a uniform 12px border and grow to a uniform 14px border on hover
  // (box 324x196, -2px margins all round); the uniform border also keeps the inner corners rounded
  // against the SCSS 24px radius (24 - 14 = 10px inner). The 2px-bordered cards keep the design's
  // heavier bottom edge (4px sides / 8px bottom). Static string literals so Tailwind's JIT keeps them.
  const hoverBorderClasses = isTargetCard
    ? 'hover:!tw-border-[14px] hover:!tw-w-[324px] hover:!tw-h-[196px] hover:tw-m-[-2px]'
    : 'hover:!tw-border-t-[4px] hover:!tw-border-x-[4px] hover:!tw-border-b-[8px] hover:!tw-w-[324px] hover:!tw-h-[196px]';

  const footer = (
    <div className={`${footerWrapperClassName}`}>
      <h3 data-testid={status}>
        {status === 'Review' ? (
          <SkillStatusBadge
            status="Revisit this"
            showIcon
            iconType="revisit"
            className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-lg tw-bg-stone-0 tw-px-2 tw-py-1 tw-text-indigo-500 tw-text-sm tw-font-bold"
          />
        ) : status === 'Not Started' ? (
          // With no other state to show (no score, not passed/inferred, not Review) surface the
          // "Practicing" badge when the student is currently practising this skill; otherwise nothing.
          isPracticing ? (
            <SkillStatusBadge
              status="Practicing"
              className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-lg tw-bg-indigo-500 tw-px-7 tw-py-1 tw-text-white tw-text-sm tw-font-bold"
            />
          ) : null
        ) : (starInfo &&
            (skillsConsolidated?.inProgress ||
              status === SkillStatesLabel.KEEP_LEARNING ||
              status === SkillStatesLabel.ALMOST_THERE)) ||
          starInfo ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
            {skillsConsolidated.skillAssessment && applyRatingStars && (
              <DashboardRatingDisplay score={assessmentScore ?? 0} className={className} />
            )}

            {!isInferred && (
              <span className="tw-font-normal tw-text-sm !tw-text-blue-600 tw-font-semibold">{starInfo.message}</span>
            )}
          </div>
        ) : (
          status
        )}
      </h3>
    </div>
  );
  // Decides what clicking this card does. `isCompletedStatus` is the dashboard-wide completion rule
  // (Passed OR Placed Out) — the same one the connectors and segment counts use — so a skill inferred
  // via diagnostic, which resolves to 'Placed Out', is treated as completed here too.
  // Completion is checked first: the progression never blocks a completed card (its index is covered
  // by the unlocked reach), but should that ever change, "you already passed this" is evidence about
  // this student, while the locked copy — "this skill might be a stretch right now" — would contradict it.
  const getSkillOption = (): SkillCardOption => {
    if (isCompletedStatus(status)) {
      return 'revisit';
    }
    return isBlocked ? 'locked' : 'normal';
  };

  const handleCardClick = (e: React.MouseEvent) => {
    const skillCode = skillsConsolidated?.skill.code;
    const activePlacement = 'post-login' as const;

    const type = getSkillOption();
    if (type === 'normal') {
      return handleBrainBreakNavigation(
        e,
        () => {
          navigate(`/skill/${skillCode}`);
        },
        activePlacement,
      );
    } else {
      setSkillModalOption({
        type: type,
        onAccept: () =>
          handleBrainBreakNavigation(
            e,
            () => {
              navigate(`/skill/${skillCode}`);
            },
            activePlacement,
          ),
      });
    }
  };

  /*e.preventDefault();
    const skillCode = skillsConsolidated?.skill.code;

    const activePlacement = 'post-login' as const;

    handleBrainBreakNavigation(
      e,
      () => {
        navigate(`/skill/${skillCode}`);
      },
      activePlacement,
    );*/

  const { IconComponent } = useSkillIcon({
    targetSkillType: skillsConsolidated?.targetSkillType,
  });

  return (
    <Card
      id={id}
      onClick={handleCardClick}
      style={
        {
          // Drives the unit-colored (or gray, when blocked) hover border defined in the className.
          '--card-hover-border-color': hoverBorderColor,
          ...(isHighlighted
            ? {
                borderColor: highlightColors.border,
                backgroundColor: highlightColors.background,
                // Shine overlay: a top-down white gradient layered over the unit color, which stays
                // visible through the gradient's transparent lower half — so the unit palette is kept.
                backgroundImage: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.35), transparent)',
                // Inner shine (like the reference `inset 5px 5px 20px`), with a stronger white (0.35
                // vs the reference's 0.1) so it reads over the light pastel backgrounds.
                boxShadow: 'inset 5px 5px 20px rgba(255, 255, 255, 0.35)',
                // Keep the target card's thick 12px SCSS border; other cards use the 3px highlight border.
                ...(isTargetCard ? {} : { borderWidth: 3 }),
              }
            : {}),
          // Blocked target cards keep their thick 12px SCSS border but swap the yellow for the
          // gray shared by the other blocked cards.
          ...(isBlocked && isTargetCard ? { borderColor: blockedCardBorderColor } : {}),
        } as CSSProperties
      }
      className={`tw-w-80 tw-h-48 tw-px-6 tw-py-4 tw-rounded-2xl tw-relative tw-inline-flex tw-justify-between tw-items-start tw-flex-wrap tw-content-start tw-transition-all tw-duration-200 hover:!tw-border-[color:var(--card-hover-border-color)] ${hoverBorderClasses} group ${
        isTargetCard
          ? 'target-card'
          : skillsConsolidated?.targetSkillType == TargetSkillTypeLabel.EXTENSION
          ? 'extension-card'
          : 'support-card'
      }`}
    >
      {/* Animated shine sweep — only on highlighted (Passed/Inferred) cards. The wrapper clips the
          rotated band to the card's rounded rect; the card itself stays overflow-visible so the
          target corner dots can keep sticking out. */}
      {isHighlighted && (
        <div ref={shineRef} className="nc_card-shine-wrapper" aria-hidden="true">
          <div className={`nc_card-shine ${isShineInView ? 'nc_card-shine--run' : ''}`} />
        </div>
      )}
      {skillsConsolidated?.targetSkillType == TargetSkillTypeLabel.TARGET ||
        (skillsConsolidated?.targetSkillType == TargetSkillTypeLabel.UNIT && (
          <>
            <div className="tw-absolute tw-w-3 tw-h-3 tw-bg-yellow-400 tw-rounded-full tw-top-[-6px] tw-left-[-6px]"></div>
            <div className="tw-absolute tw-w-3 tw-h-3 tw-bg-yellow-400 tw-rounded-full tw-top-[-6px] tw-right-[-6px]"></div>
            <div className="tw-absolute tw-w-3 tw-h-3 tw-bg-yellow-400 tw-rounded-full tw-bottom-[-6px] tw-left-[-6px]"></div>
            <div className="tw-absolute tw-w-3 tw-h-3 tw-bg-yellow-400 tw-rounded-full tw-bottom-[-6px] tw-right-[-6px]"></div>
          </>
        ))}

      {/* The exit-ticket badge, straddling the card's top edge. Shown on blocked cards too: it says
          this skill can be inferred from exit tickets, which is true whether or not the progression
          has reached the card yet. */}
      {exitSlipProgress && (
        <DashboardCardExitSlipBadge
          progress={exitSlipProgress}
          skillName={skillsConsolidated?.skill.name ?? ''}
          skillCode={skillsConsolidated?.skill.code ?? ''}
          // Half of the ON FIRE rule lives out here: the fire points at a skill within reach, so a
          // skill the student already holds is not one to point at. Same completion rule the unit
          // header's counts and the progress bar use, so the three never disagree.
          skillAlreadyEarned={earnedViaExitSlip || isSkillCompleted(skillsConsolidated)}
        />
      )}

      {/* Icon at the top, centered */}
      <div className="nc_card-icons tw-flex tw-w-full tw-justify-center tw-items-center">
        {isBlocked ? (
          <Lock />
        ) : isInferred || status === 'Passed' ? (
          <Check className="tw-w-6 tw-h-6" />
        ) : (
          <IconComponent />
        )}
      </div>
      {/* Skill name in the middle, centered */}
      <p
        className={`tw-w-full tw-text-center ${titleColorClass} tw-text-base tw-font-bold tw-font-['Atkinson_Hyperlegible'] tw-leading-tight`}
        tabIndex={0}
        aria-label={skillsConsolidated?.skill.name}
      >
        {skillsConsolidated?.skill.name}
      </p>
      {/* Score at the bottom, centered */}
      {!isBlocked && <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-2">{footer}</div>}
      {skillsConsolidated?.refresher && starInfo?.stars !== 5 && (
        <div className="refresher-bottom tw-flex tw-flex-row tw-justify-center tw-self-center tw-items-center">
          <h4 className="tw-font-['Atkinson_Hyperlegible']">Take skill challenge</h4>
          <ArrowRight className="tw-h-4 tw-rounded-full tw-inline-flex tw-justify-center tw-items-center tw-gap-2 tw-overflow-hidden" />
        </div>
      )}
      <DashboardSkillCardModal
        setSkillModalState={setSkillModalOption}
        skillModalState={skillModalOption}
        hasPassedConnector={hasPassedConnector ?? false}
      />
    </Card>
  );
};

export default DashboardCard;
